import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounce } from "./use-debounce";

// in pixels, default tailwind, if changed needs to change as well
export const breakpoints = {
  xs: 540,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
} as const;

export type Breakpoint = keyof typeof breakpoints;

export default function useBreakpoints(debounce: number = 100) {
  const ordered = useMemo(
    () => Object.entries(breakpoints).sort(([, a], [, b]) => a - b),
    [breakpoints],
  );
  const debounced = useDebounce();
  const getBreakpoint = useCallback(
    (width: number): (typeof ordered)[number] => {
      let left = 0;
      let right = ordered.length - 1;

      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        if (ordered[mid][1] === width) {
          return ordered[mid];
        } else if (ordered[mid][1] < width) {
          left = mid + 1;
        } else {
          right = mid - 1;
        }
      }

      return left < ordered.length
        ? ordered[left]
        : ordered[ordered.length - 1]; // Return the size just above the width
    },
    [ordered],
  );
  const [bp, set] = useState(getBreakpoint(window.innerWidth));
  useEffect(() => {
    const handler = () =>
      debounced(debounce, async () => set(getBreakpoint(window.innerWidth)));
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [bp]);
  return bp?.[0];
}
